// src/lib/pharmacyFirst/pathways/aht/aht-wt-tirzepatide-transfer.pathway.ts
//
// AHT Weight Management — Tirzepatide (Mounjaro®) Transfer Supply Pathway
// Source: AHT_Mounjaro_Clinical_Pathway__1_.docx v1.4
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.
// For patients transferring from another provider already established on tirzepatide.

import type { Pathway } from "../../types/pathway.types";

// ─────────────────────────────────────────────────────────────────────────────
// Node IDs
// ─────────────────────────────────────────────────────────────────────────────

const Q0_RED_FLAGS     = "tzp-trans-q0-red-flags";
const Q0A_BMI_FLOOR    = "tzp-trans-q0a-bmi-floor";
const Q1_F2F           = "tzp-trans-q1-f2f";
const CONFIRM_CARD     = "tzp-trans-confirmation-card";
const PGD_FRESH_CHECKS = "tzp-trans-pgd-fresh-checks";
const Q3_INT_NTI       = "tzp-trans-int1-narrow-ti";
const Q4_INT_OCP       = "tzp-trans-int2-contraceptives";
const Q5_CAUTIONS      = "tzp-trans-cautions";
const Q6_TRANSFER_FLAG = "tzp-trans-transfer-flag";
const Q7_DOSE          = "tzp-trans-dose-select";
const Q8_ATTEST        = "tzp-trans-attest";

const OUT_NO_F2F        = "OUT_CANNOT_NOT_F2F";
const OUT_CANNOT_SUPPLY = "OUT_CANNOT_SUPPLY";
const OUT_SUPPLY        = "OUT_SUPPLY";

// ─────────────────────────────────────────────────────────────────────────────
// Pathway
// ─────────────────────────────────────────────────────────────────────────────

export const ahtWtTirzepatideTransferPathway: Pathway = {
  id: "aht-wt-tirzepatide-transfer",
  name: "Mounjaro® (tirzepatide) — Transfer Supply",
  population: "Adults aged 18–84 transferring from another provider",
  pgdRefs: ["pgd-aht-wt-tirzepatide"],
  demographicConfiguration: { bannerFields: [] },
  entryNode: Q0_RED_FLAGS,

  nodes: [
    // ─────────────────────────────────────────────────────────────────────────
    // Step 0 — Red flags gateway
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q0_RED_FLAGS,
      type: "gateway_progression",
      triggersPayment: false,
      heading: "Check for red flags before proceeding",
      body:
        "Before proceeding with transfer supply, confirm the patient has none of the following:\n" +
        "• Severe or persistent abdominal pain (possible pancreatitis — refer urgently)\n" +
        "• Signs of allergic reaction or anaphylaxis — do not supply\n" +
        "• Pregnancy confirmed or suspected — discontinue\n" +
        "• New diagnosis of thyroid cancer, MEN2, pancreatitis, or Type 1 diabetes\n" +
        "If any red flag is present, do not proceed — refer to GP or A&E as appropriate.",
      next: Q0A_BMI_FLOOR,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 0a — BMI floor exclusion (transfer patients only)
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q0A_BMI_FLOOR,
      type: "checklist",
      text: "Does any of the following apply to this patient?",
      hint: "Select if present. Tirzepatide (Mounjaro®) must be discontinued if BMI falls below 22 kg/m² during treatment. If this applies, supply cannot proceed.",
      items: [
        {
          id: "tzp-trans-bmi-floor",
          text: "BMI has fallen below 22 kg/m² since starting treatment",
        },
      ],
      includeNoneOption: true,
      noneOptionLabel: "None of the above — no exclusion criteria present",
      anySelected: { outcomeId: OUT_CANNOT_SUPPLY },
      noneSelected: Q1_F2F,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 1 — Face-to-face confirmation
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q1_F2F,
      type: "condition_route",
      conditions: [
        {
          evaluate: (ctx) => ctx.context["sharedFaceToFace"] === false,
          then: { outcomeId: OUT_NO_F2F },
        },
      ],
      default: "tzp-trans-q-thyroid",
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Steps 2a–2g — Clinical screening (replaces confirmation card)
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: "tzp-trans-q-thyroid",
      type: "yes_no",
      text: "Does the patient have a personal or family history of thyroid cancer or MEN2?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: "tzp-trans-q-pancreatitis",
    },
    {
      id: "tzp-trans-q-pancreatitis",
      type: "yes_no",
      text: "Does the patient have a history of pancreatitis (acute or chronic)?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: "tzp-trans-q-t1dm",
    },
    {
      id: "tzp-trans-q-t1dm",
      type: "yes_no",
      text: "Does the patient have Type 1 or Type 3c diabetes mellitus?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: "tzp-trans-q-renal",
    },
    {
      id: "tzp-trans-q-renal",
      type: "yes_no",
      text: "Does the patient have severe renal impairment (CrCl <15 mL/min) or severe hepatic impairment?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: "tzp-trans-q-gi",
    },
    {
      id: "tzp-trans-q-gi",
      type: "yes_no",
      text: "Does the patient have severe or unstable gastrointestinal disease including gastroparesis?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: "tzp-trans-q-pregnancy",
    },
    {
      id: "tzp-trans-q-pregnancy",
      type: "yes_no",
      text: "Is the patient pregnant, breastfeeding, or planning pregnancy?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: "tzp-trans-q-retinopathy",
    },
    {
      id: "tzp-trans-q-retinopathy",
      type: "yes_no",
      text: "Does the patient have diabetic retinopathy or macular oedema?",
      yes: { outcomeId: OUT_CANNOT_SUPPLY },
      no: PGD_FRESH_CHECKS,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 2b — PGD inclusion / exclusion checks
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: PGD_FRESH_CHECKS,
      type: "pgd_exclusion_check",
      variant: "sequential",
      pgdIdOverride: "pgd-aht-wt-tirzepatide",
      anyTriggered: { outcomeId: OUT_CANNOT_SUPPLY },
      allCleared: Q3_INT_NTI,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 3 — Interaction checks
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q3_INT_NTI,
      type: "yes_no",
      text: "Is the patient taking any oral medicines with a narrow therapeutic index?",
      hint: "Tirzepatide delays gastric emptying — may affect absorption of oral medicines.",
      managementPlan: {
        triggeredByAnswer: "yes",
        planPrompt: "Document the monitoring and management plan for this interaction",
        acknowledgementText: "Patient has been counselled on this interaction and understands the management plan",
      },
      yes: Q4_INT_OCP,
      no: Q4_INT_OCP,
    },

    {
      id: Q4_INT_OCP,
      type: "yes_no",
      text: "Is the patient using oral contraceptives?",
      hint: "Reduced efficacy cannot be excluded — add barrier method for 4 weeks after starting or any dose increase.",
      managementPlan: {
        triggeredByAnswer: "yes",
        planPrompt: "Document the contraceptive counselling and management plan",
        acknowledgementText: "Patient has been counselled on this interaction and understands the management plan",
      },
      yes: Q5_CAUTIONS,
      no: Q5_CAUTIONS,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 4 — Cautions review
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q5_CAUTIONS,
      type: "flag",
      variant: "warn",
      message:
        "Review cautions before supply:\n" +
        "• GI symptoms (nausea, vomiting, diarrhoea) — dehydration risk\n" +
        "• Gastroparesis symptoms (bloating, abdominal pain) — discontinue if develops\n" +
        "• Suspected allergic reaction — discontinue\n" +
        "• Pancreatitis suspected — stop immediately; do not restart if confirmed\n" +
        "• Monitor resting heart rate\n" +
        "• Report mood or behavioural changes\n" +
        "• Not a substitute for insulin\n" +
        "• Aspiration risk — advise delayed gastric emptying before GA or deep sedation\n" +
        "• Renal function may deteriorate with prolonged GI effects",
      next: Q6_TRANSFER_FLAG,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 5 — Transfer notice
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q6_TRANSFER_FLAG,
      type: "flag",
      variant: "info",
      message:
        "Transfer from another provider — patient is already established on this medication.\n" +
        "Confirm the patient's current dose before supplying.\n" +
        "• Request evidence of previous supply (prescription, dispensing label, or transfer letter)\n" +
        "• Verify the dose matches the patient's stated current regimen\n" +
        "• Supply at the established dose — do not restart dose titration",
      next: Q7_DOSE,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 6 — Current dose selection
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q7_DOSE,
      type: "single_select",
      text: "What dose should be supplied?",
      options: [
        { id: "tzp-2_5mg",  label: "2.5 mg SC once weekly" },
        { id: "tzp-5mg",    label: "5 mg SC once weekly" },
        { id: "tzp-7_5mg",  label: "7.5 mg SC once weekly" },
        { id: "tzp-10mg",   label: "10 mg SC once weekly" },
        { id: "tzp-12_5mg", label: "12.5 mg SC once weekly" },
        { id: "tzp-15mg",   label: "15 mg SC once weekly" },
      ],
      routing: {
        "tzp-2_5mg":  Q8_ATTEST,
        "tzp-5mg":    Q8_ATTEST,
        "tzp-7_5mg":  Q8_ATTEST,
        "tzp-10mg":   Q8_ATTEST,
        "tzp-12_5mg": Q8_ATTEST,
        "tzp-15mg":   Q8_ATTEST,
      },
      storesTo: "selectedDoseStep",
      required: true,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 7 — Supply attestation
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: Q8_ATTEST,
      type: "attestation",
      text: "Confirm transfer supply of Mounjaro® (tirzepatide)",
      attestationStatement:
        "I confirm that I have:\n" +
        "• Supplied 1 × Mounjaro® KwikPen at the patient's confirmed established dose (4 doses = 28-day supply)\n" +
        "• Verified the dose against evidence of previous supply\n" +
        "• Checked pen integrity and cold chain before supply\n" +
        "• Confirmed storage: 2–8°C before first use; after first use ≤30°C or refrigerated up to 30 days\n" +
        "• Counselled the patient on injection technique and injection site rotation\n" +
        "• Advised the patient to inform their GP of the provider transfer\n" +
        "• Provided patient information leaflet\n" +
        "• Recorded batch number and expiry date",
      conclusions: {
        optionA: { label: "Confirm and supply", next: { outcomeId: OUT_SUPPLY } },
        optionB: { label: "Do not supply",      next: { outcomeId: OUT_CANNOT_SUPPLY } },
      },
    },
  ],

  outcomes: {
    [OUT_NO_F2F]: {
      id: OUT_NO_F2F,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply — face-to-face consultation required",
      reason: "Supply under this PGD requires the patient to attend in person at AHT registered premises.",
      safetyNetting: ["Ask the patient to book an in-person appointment to proceed with supply."],
    },
    [OUT_CANNOT_SUPPLY]: {
      id: OUT_CANNOT_SUPPLY,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply — Mounjaro® (tirzepatide)",
      reason: "An inclusion criterion was not met or an exclusion criterion was triggered. Supply cannot proceed under this PGD.",
      safetyNetting: [
        "Discuss the reason supply cannot proceed with the patient.",
        "Consider referral to prescriber or GP if clinically appropriate.",
      ],
    },
    [OUT_SUPPLY]: {
      id: OUT_SUPPLY,
      type: "outcome",
      outcomeType: "supply",
      title: "Transfer supply complete — Mounjaro® (tirzepatide)",
      reason: "All inclusion criteria met, no exclusion criteria triggered, and transfer supply attestation confirmed.",
      drugRef: "tirzepatide",
      safetyNetting: [
        "Return in 4 weeks for monthly monitoring review.",
        "Contact the service immediately if any signs of allergic reaction, severe abdominal pain, or persistent vomiting.",
        "Seek urgent medical attention for signs of pancreatitis: severe, persistent abdominal pain radiating to the back.",
        "Store unused pens in the refrigerator (2–8°C). After first use, may store at ≤30°C for up to 30 days.",
      ],
    },
  },
};
